import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, User, BookOpen, Quote } from "lucide-react";
import { API } from "@/lib/api";
import { Eyebrow } from "@/components/common";
import Markdown from "@/components/Markdown";
import { IMAGES } from "@/data/content";

const SUGGESTIONS = [
  "What is dharma, and why is it hard to translate?",
  "Summarise the Four Noble Truths of Buddhism.",
  "How does Kashmir Shaivism understand consciousness?",
  "What does the Holy Book say about the limits of authority?",
];

export default function Scholar() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(Math.random().toString(36).slice(2));

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const next = [...messages, { role: "user", content: q }, { role: "assistant", content: "", citations: [] }];
    setMessages(next);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/scholar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, history, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let raw = "";
      let citations = [];
      let citeDone = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        if (!citeDone && raw.startsWith("\x1e")) {
          const end = raw.indexOf("\x1e", 1);
          if (end === -1) continue; // wait for full header
          try { citations = JSON.parse(raw.slice(1, end)).citations || []; } catch { citations = []; }
          raw = raw.slice(end + 1);
          citeDone = true;
        } else if (!citeDone) {
          citeDone = true; // no header present
        }
        const answer = raw;
        setMessages((cur) => { const c = [...cur]; c[c.length - 1] = { role: "assistant", content: answer, citations }; return c; });
      }
    } catch {
      setMessages((cur) => { const c = [...cur]; c[c.length - 1] = { role: "assistant", content: "INDIC Scholar is temporarily unavailable. Please try again shortly.", citations: [] }; return c; });
    } finally { setStreaming(false); }
  };

  const empty = messages.length === 0;

  return (
    <div className="pt-16 lg:pt-[72px] min-h-screen bg-indic-indigo flex flex-col" data-testid="scholar-page">
      <div className="flex-1 flex flex-col mx-auto w-full max-w-3xl px-4 sm:px-6">
        {empty ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
            <div className="relative">
              <img src={IMAGES.mandala} alt="" className="h-28 w-28 rounded-full object-cover opacity-30" />
              <Sparkles className="absolute inset-0 m-auto text-indic-gold" size={30} />
            </div>
            <Eyebrow className="mt-8 block">Knowledge Without Boundaries</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-semibold text-amber-50">INDIC Scholar</h1>
            <p className="mt-4 max-w-lg text-slate-300/90 leading-relaxed">
              An AI research assistant grounded in the INDIC library. Ask about philosophy, history, traditions, or any passage — answers are sourced and mark fact from interpretation.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-3 w-full max-w-xl">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="rounded-sm border border-indic-card-border bg-indic-card p-4 text-left text-sm text-slate-300 hover:border-indic-gold/40 hover:text-amber-50 transition-colors" data-testid="scholar-suggestion">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 overflow-y-auto py-10 space-y-8" data-testid="scholar-messages">
            {messages.map((m, i) => (
              <div key={i} className="flex gap-4" data-testid={`scholar-msg-${m.role}`}>
                <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${m.role === "user" ? "bg-indic-card border border-indic-card-border text-slate-300" : "bg-indic-gold/15 border border-indic-gold/40 text-indic-gold"}`}>
                  {m.role === "user" ? <User size={16} /> : <Sparkles size={16} />}
                </div>
                <div className="flex-1 pt-1">
                  <div className="text-xs font-sans uppercase tracking-wider text-slate-500 mb-1">{m.role === "user" ? "You" : "INDIC Scholar"}</div>
                  {m.role === "assistant" ? (
                    m.content ? <div className="text-slate-200/90 leading-relaxed"><Markdown text={m.content} /></div>
                      : <div className="flex gap-1.5 pt-2"><span className="h-2 w-2 rounded-full bg-indic-gold/60 animate-bounce" /><span className="h-2 w-2 rounded-full bg-indic-gold/60 animate-bounce [animation-delay:0.15s]" /><span className="h-2 w-2 rounded-full bg-indic-gold/60 animate-bounce [animation-delay:0.3s]" /></div>
                  ) : <p className="text-amber-50 leading-relaxed">{m.content}</p>}
                  {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                    <div className="mt-5" data-testid="scholar-citations">
                      <div className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-wider text-indic-gold/80 mb-2"><Quote size={12} /> Cited passages</div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {m.citations.map((c, ci) => (
                          <Link key={ci} to={`/read/${c.doc_id}?focus=${encodeURIComponent((c.excerpt || "").replace(/…$/, "").slice(0, 90))}`} className="block rounded-sm border border-indic-card-border bg-indic-card p-3 hover:border-indic-gold/40 transition-colors" data-testid={`citation-${ci}`}>
                            <div className="text-[11px] font-sans uppercase tracking-wide text-indic-sandstone/80">{c.work} · {c.group_title}</div>
                            <div className="font-serif text-amber-50 text-sm mt-0.5">{c.code ? `${c.code} — ` : ""}{c.doc_title}</div>
                            <div className="text-xs text-slate-400 mt-1 line-clamp-2">{c.excerpt}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="sticky bottom-0 pb-6 pt-3 bg-gradient-to-t from-indic-indigo via-indic-indigo to-transparent">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="relative">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about Indic civilisation, philosophy, or a text…"
              className="w-full rounded-sm border border-indic-card-border bg-indic-card py-4 pl-5 pr-14 text-amber-50 placeholder:text-slate-500 focus:border-indic-gold/60 focus:outline-none" data-testid="scholar-input" disabled={streaming} />
            <button type="submit" disabled={streaming || !input.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-sm bg-indic-gold text-indic-indigo disabled:opacity-40 hover:bg-indic-gold-soft transition-colors" data-testid="scholar-send">
              <Send size={17} />
            </button>
          </form>
          <p className="mt-2 text-center text-xs text-slate-600 flex items-center justify-center gap-1.5"><BookOpen size={12} /> Grounded in the INDIC library · Claude Sonnet 5.5 · May occasionally err — verify important claims</p>
        </div>
      </div>
    </div>
  );
}
